import React from 'react';
import styled from '@emotion/styled';
import { IChannel } from '../types';
import { format } from 'date-fns';
import { theme, ArrowBackwardIcon, ArrowForwardIcon } from '@arnold/common';
import { Answer } from '../reports/components/RespondentsAnswers';
import { useTranslation } from 'react-i18next';
import { GetMessagesQuery } from '../generated/hooks';
import { hidePipeSymbols } from '../lib/questionText';

type Reaction = NonNullable<IChannel['reactions']>[number];

type ChatMessage = NonNullable<GetMessagesQuery['messages']>[number];

interface ChannelReactionsProps {
  channel: IChannel;
  messages: Array<ChatMessage> | null | undefined;
}

const BoxWrapper = styled.div`
  background-color: white;
`;

const Box = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${theme.spacing.f} 0px ${theme.spacing.h} 0px;
  gap: ${theme.spacing.g};
  width: 90vw;
  max-width: 960px;
  margin-left: auto;
  margin-right: auto;
`;

const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;

  @media (max-width: ${theme.breakpoint.small}) {
    flex-direction: column;
    align-items: center;
    gap: ${theme.spacing.f};
  }
`;

const Pager = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${theme.spacing.f};

  span {
    font-size: ${theme.typography.body.medium.regular.fontSize};
    font-weight: ${theme.typography.body.medium.regular.fontWeight};
    color: ${theme.colors.text.primary};
    line-height: ${theme.typography.body.medium.regular.lineHeight};
  }
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
`;

const ColumnsRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${theme.spacing.f};
`;

const Column = styled.div<{ alignEnd?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  flex: 0 0 55%;
  width: 55%;
  ${(p) => (p.alignEnd ? 'margin-left: auto;' : '')}
  @media (max-width: ${theme.breakpoint.small}) {
    flex: 0 0 100%;
    width: 100%;
    ${(p) => (p.alignEnd ? 'padding-left: 24px;' : '')}
  }
`;

const CenterColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  @media (max-width: ${theme.breakpoint.small}) {
    align-items: flex-start;
  }
`;

const CustomSpan = styled.span`
  font-size: ${theme.typography.body.small.regular.fontSize};
  font-weight: ${theme.typography.body.small.regular.fontWeight};
  color: ${theme.colors.text.secondary};
  line-height: ${theme.typography.body.small.regular.lineHeight};
  margin-bottom: 7px;
  align-self: flex-start;
`;

const DateRight = styled.span`
  align-self: flex-end;
  white-space: nowrap;
  color: ${theme.colors.text.secondary};
  font-size: ${theme.typography.body.small.regular.fontSize};
  line-height: ${theme.typography.body.small.regular.lineHeight};
`;

const AnswerWithDate = styled.div`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-end;
  width: fit-content;
  max-width: 100%;
  gap: 7px;
  @media (max-width: ${theme.breakpoint.small}) {
    width: 100%;
    align-items: stretch;
    & > div {
      width: 100% !important;
    }
  }
`;

const OverallAnswerBlock = styled.div`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  width: fit-content;
  max-width: 100%;
  @media (max-width: ${theme.breakpoint.small}) {
    width: 100%;
  }
`;

const ArrowWrapper = styled.div`
  width: 40px;
  height: 32px;
  padding: ${theme.spacing.d} ${theme.spacing.e};
  border-radius: ${theme.borderRadius.l};
  background-color: ${theme.colors.backgroundCover.default};
  border: 1px solid ${theme.colors.borderMain.default};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  svg * {
    fill: ${theme.colors.iconText.primary} !important;
  }
`;

export const ChannelReactions = ({ channel, messages }: ChannelReactionsProps) => {
  const reactionsList: Reaction[] = React.useMemo(() => channel?.reactions ?? [], [channel]);
  const [index, setIndex] = React.useState(() => {
    const url = new URL(window.location.href);
    const reactionIdParam = url.searchParams.get('reactionId');
    if (reactionIdParam) {
      const indexById = reactionsList.findIndex((r) => String(r.id) === reactionIdParam);
      return indexById >= 0 ? indexById : 0;
    }
    return 0;
  });
  const total = reactionsList.length;

  const { t } = useTranslation('Report');

  const { messageById } = React.useMemo(() => {
    const byId = new Map<string, ChatMessage>();
    (messages ?? []).forEach((message: ChatMessage) => {
      if (message && message.id) {
        byId.set(message.id, message);
      }
    });
    return { messageById: byId };
  }, [messages]);

  if (!reactionsList || reactionsList.length === 0) return null;

  const reaction = reactionsList[Math.max(0, Math.min(index, total - 1))];
  const relatedMessage = reaction?.messageId ? messageById.get(reaction.messageId) : undefined;

  const formatDate = (iso?: string | null) => {
    if (!iso) return '';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '' : format(d, 'dd.MM.yyyy');
  };

  const isOverallReaction = !reaction?.messageId;

  const computeQuestionText = (): string => {
    if (isOverallReaction) return '';
    const m = relatedMessage;
    if (!m) return '';

    const idx = 'statementIndex' in m && typeof m.statementIndex === 'number' ? m.statementIndex : null;
    const def = 'question' in m && m.question?.definition;
    if (
      def &&
      def?.__typename === 'ScaleQuestionDefinition' &&
      Array.isArray(def.statements) &&
      idx != null &&
      idx >= 0 &&
      idx < def.statements.length
    ) {
      return hidePipeSymbols(def.statements[idx] ?? '');
    }
    return def && 'text' in def ? hidePipeSymbols(def.text) : '';
  };

  const computeAnswerText = (): string => {
    if (isOverallReaction) return '';
    const m = relatedMessage;
    if (!m) return '';
    const text = typeof m.text === 'string' ? m.text : '';
    if (text.trim() !== '') return text;
    return String('value' in m && m.value != null ? m.value : '');
  };

  const questionText = computeQuestionText();
  const rawAnswer = computeAnswerText();
  const isScaleContext =
    (relatedMessage &&
      'question' in relatedMessage &&
      relatedMessage?.question?.definition?.__typename === 'ScaleQuestionDefinition') ||
    /ScaleAnswer$/i.test(relatedMessage?.__typename ?? '');
  const answerFromMessage =
    isScaleContext && (rawAnswer == null || String(rawAnswer).trim() === '')
      ? relatedMessage &&
        'question' in relatedMessage &&
        relatedMessage.question?.definition?.__typename === 'ScaleQuestionDefinition' &&
        (relatedMessage.question.definition.showEscapeOption ?? true)
        ? t('bubble:scaleEscapeOption')
        : ''
      : String(rawAnswer ?? '');

  const goPrev = () => setIndex((i) => (i - 1 + total) % total);
  const goNext = () => setIndex((i) => (i + 1) % total);

  return (
    <BoxWrapper>
      <Box data-cy="channel-reactions">
        <HeaderRow>
          <h2>{t('reactionsFromManager')}</h2>
          <Pager>
            <ArrowWrapper role="button" onClick={goPrev} aria-label="prev">
              <ArrowBackwardIcon data-cy="channel-reactions-prev-button" />
            </ArrowWrapper>
            <span data-cy="channel-reactions-pages-count">{total > 0 ? `${index + 1}/${total}` : '0/0'}</span>
            <ArrowWrapper role="button" onClick={goNext} aria-label="next">
              <ArrowForwardIcon data-cy="channel-reactions-next-button" />
            </ArrowWrapper>
          </Pager>
        </HeaderRow>

        {isOverallReaction ? (
          <Body>
            <CenterColumn>
              <OverallAnswerBlock>
                <CustomSpan>{t('reactionToWholeSurvey')}</CustomSpan>
                <CustomSpan>{reaction?.respondentFullName ?? ''}</CustomSpan>
                <AnswerWithDate>
                  <Answer data-cy="channel-reactions-text-overall">{reaction?.text ?? ''}</Answer>
                  <DateRight>{formatDate(reaction?.createdAt)}</DateRight>
                </AnswerWithDate>
              </OverallAnswerBlock>
            </CenterColumn>
          </Body>
        ) : (
          <Body>
            <ColumnsRow>
              <Column>
                <CustomSpan>{questionText}</CustomSpan>
                <Answer style={{ width: '100%' }}>{answerFromMessage}</Answer>
              </Column>

              <Column alignEnd>
                <CustomSpan>{reaction?.respondentFullName ?? ''}</CustomSpan>
                <AnswerWithDate style={{ width: '100%' }}>
                  <Answer style={{ width: '100%' }} data-cy="channel-reactions-text-message">
                    {reaction?.text ?? ''}
                  </Answer>
                  <DateRight>{formatDate(reaction?.createdAt)}</DateRight>
                </AnswerWithDate>
              </Column>
            </ColumnsRow>
          </Body>
        )}
      </Box>
    </BoxWrapper>
  );
};
